import {ActionIcon, Menu} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {TimeInput} from '@mantine/dates';
import {IconDotsVertical} from '@tabler/icons-react';
import React from 'react';
import {Pill} from 'src/design/atoms/Pill';
import {formatUtcShortDate} from 'src/shared/utils/formatters';
import {padTwo} from 'src/shared/utils/utcDate';

export interface DispatchCardMeta {
  id: number;
  siteName: string;
  inspectionTypeName: string;
  dueAt?: Date;
}

export interface DispatchCardProps {
  meta: DispatchCardMeta;
  scheduledStartAt: Date;
  isTentative: boolean;
  isPastDue: boolean;
  isHighlighted?: boolean;
  /** Show an editable time control (Day view: editing relocates the card). */
  showTimeControl: boolean;
  onSetTime?: (hour: number, minute: number) => void;
  onReschedule: () => void;
  onUnassign: () => void;
  onRemoveTentative: () => void;
}

export const DispatchCard: React.FC<DispatchCardProps> = ({
  meta,
  scheduledStartAt,
  isTentative,
  isPastDue,
  isHighlighted,
  showTimeControl,
  onSetTime,
  onReschedule,
  onUnassign,
  onRemoveTentative,
}) => {
  const timeValue = `${padTwo(scheduledStartAt.getUTCHours())}:${padTwo(
    scheduledStartAt.getUTCMinutes()
  )}`;

  return (
    <Paper
      withBorder
      p="xs"
      shadow="xs"
      style={{
        borderStyle: isTentative ? 'dashed' : 'solid',
        borderColor: isHighlighted
          ? 'var(--mantine-color-red-6)'
          : isTentative
          ? 'var(--mantine-color-yellow-6)'
          : undefined,
        borderWidth: isHighlighted ? 2 : undefined,
      }}>
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Stack gap={2} style={{minWidth: 0}}>
          <Text size="sm" fw={600} truncate>
            {meta.siteName}
          </Text>
          <Text size="xs" c="dimmed" truncate>
            {meta.inspectionTypeName}
          </Text>
        </Stack>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" aria-label="Card actions">
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={onReschedule}>Reschedule…</Menu.Item>
            {isTentative ? (
              <Menu.Item color="red" onClick={onRemoveTentative}>
                Discard change
              </Menu.Item>
            ) : (
              <Menu.Item color="red" onClick={onUnassign}>
                Unassign
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Group gap="xs" mt={4}>
        {isTentative && <Pill text="Tentative" color="yellow" />}
        {isPastDue && <Pill text="Past due" color="red" />}
        <Text size="xs" c="dimmed">
          Due {formatUtcShortDate(meta.dueAt)}
        </Text>
      </Group>

      {showTimeControl && isTentative && (
        <TimeInput
          mt={6}
          size="xs"
          aria-label="Scheduled time"
          value={timeValue}
          onChange={event => {
            const [h, m] = event.currentTarget.value.split(':');
            if (h !== undefined && m !== undefined && h !== '' && m !== '') {
              onSetTime?.(Number(h), Number(m));
            }
          }}
        />
      )}
    </Paper>
  );
};
